import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, Redirect, useParams } from "react-router-dom";
import Iframe from "react-iframe";
import {
  FaCheck,
  FaIdCard,
  FaUserGraduate,
  FaUsers,
  FaUserPlus,
} from "react-icons/fa";
import MyLoader from "../Spinner/MyLoader";
import COLORS from "../resources/colors/COLORS";
import SessionManager from "../Utils/SessionManager";
import myToast from "../Utils/MyToast";
import BackButton from "../ChildComponent/BackButton";
import MdlSelectMultipleStudent from "../dialog/MdlSelectMultipleStudent";
import "../css/Admin_css/admin-customize.css";
import "../css/Admin_css/payroll.css";

const DOCUMENT_TYPE = "STUDENT_ID_CARD";
const SELECTION_TYPE = "student";
const DEFAULT_IFRAME_HEIGHT = "300px";
const API_BASE = () =>
  `${process.env.REACT_APP_SERVER_NAME}${process.env.REACT_APP_TARGET_DIR}`;
const SERVER_NAME = process.env.REACT_APP_SERVER_NAME;
const SYSTEM_CODE = process.env.REACT_APP_SYSTEM_CODE;

const HERO_GRADIENT = `linear-gradient(135deg, ${COLORS.primary_color_shade5} 0%, ${COLORS.primary_color_shade7} 55%, ${COLORS.primary_color_shade9} 100%)`;
const PAGE_FONT =
  'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

//Created By Cursor
function decodeParam(value) {
  if (value == null || value === "") return "";
  try {
    return decodeURIComponent(String(value));
  } catch {
    return String(value);
  }
}

//Created By Cursor
function StudentIdCard() {
  const loggedIn = localStorage.getItem("token") != null;
  const params = useParams();
  const routeStudentId = decodeParam(
    params.student_id || params.Enrollment_no || ""
  );
  const routeSessionId = decodeParam(
    params.session_id || params.Session_id || ""
  );

  const [loading, setLoading] = useState(true);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [isOpenSelectStudent, setIsOpenSelectStudent] = useState(false);
  const [samples, setSamples] = useState([]);
  const [sampleIndex, setSampleIndex] = useState(0);
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const [iframeHeight, setIframeHeight] = useState(DEFAULT_IFRAME_HEIGHT);

  const selectedSample = samples[sampleIndex] || null;
  const primaryStudent = selectedStudents[0] || null;
  const enrollmentNo =
    primaryStudent?.student_enrollment_no || routeStudentId || "";
  const sessionId = primaryStudent?.session_id || routeSessionId || "";

  //Created By Cursor
  const buildFileUrl = useCallback((sample, students) => {
    if (!sample?.file_name || !sample?.file_directory) return "";
    const list = Array.isArray(students) ? students : [];
    if (!list.length) return "";

    const schoolCode = SessionManager.getSchoolCode();
    const studentIds = list.map((s) => s.student_enrollment_no);
    const sid = list[0]?.session_id || "";

    return (
      `${SERVER_NAME}${sample.file_directory}${sample.file_name}` +
      `?&student_ids=${JSON.stringify(studentIds)}` +
      `&class_id=&section_id=` +
      `&session_id=${sid}` +
      `&sample_id=${sample.sample_id || ""}` +
      `&selection_type=${SELECTION_TYPE}` +
      `&system_code=${SYSTEM_CODE || ""}` +
      `&school_code=${schoolCode || ""}`
    );
  }, []);

  //Created By Cursor
  const applyPreview = useCallback(
    (list, nextSampleIndex, students) => {
      const sample = list?.[nextSampleIndex];
      const url = buildFileUrl(sample, students);
      setIframeHeight(DEFAULT_IFRAME_HEIGHT);
      setPreviewLoading(Boolean(url));
      setFileUrl(url);
      if (url) console.log("[StudentIdCard] FILE_URL", url);
    },
    [buildFileUrl]
  );

  //Created By Cursor
  const loadSamples = useCallback(async () => {
    const schoolCode = SessionManager.getSchoolCode();
    if (!schoolCode) {
      myToast.notify_warning("School code is missing.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const response = await axios.get(`${API_BASE()}getPrintableSample.php`, {
        params: {
          school_code: schoolCode,
          document_type: DOCUMENT_TYPE,
        },
      });

      if (response.data?.api_status === "OK") {
        const list = Array.isArray(response.data.data)
          ? response.data.data
          : [];
        setSamples(list);
        setSampleIndex(0);

        const seeded = routeStudentId
          ? [
              {
                student_enrollment_no: routeStudentId,
                session_id: routeSessionId || "",
              },
            ]
          : [];

        if (seeded.length) {
          setSelectedStudents(seeded);
        }

        if (list.length) {
          applyPreview(list, 0, seeded);
        } else {
          myToast.notify_warning("No ID card templates found.");
        }
      } else {
        setSamples([]);
        setFileUrl("");
        myToast.notify_warning(
          response.data?.api_remark || "Could not load ID card samples."
        );
      }
    } catch (error) {
      console.error(error);
      myToast.notify_warning("Network error loading ID card samples.");
    } finally {
      setLoading(false);
    }
  }, [routeStudentId, routeSessionId, applyPreview]);

  useEffect(() => {
    loadSamples();
  }, [loadSamples]);

  //Created By Cursor
  useEffect(() => {
    const onMessage = (event) => {
      if (event.data?.iframeHeight) {
        setIframeHeight(`${event.data.iframeHeight}px`);
      }
    };
    window.addEventListener("message", onMessage, false);
    return () => window.removeEventListener("message", onMessage);
  }, []);


  //Created By Cursor
  const handleSampleSelect = useCallback(
    (index) => {
      setSampleIndex(index);
      applyPreview(samples, index, selectedStudents);
    },
    [samples, selectedStudents, applyPreview]
  );

  //Created By Cursor
  const handleSelectedStudent = useCallback(
    (students) => {
      const next = Array.isArray(students) ? students : [];
      setIsOpenSelectStudent(false);
      setSelectedStudents(next);
      applyPreview(samples, sampleIndex, next);
    },
    [samples, sampleIndex, applyPreview]
  );

  const selectedCountLabel = useMemo(() => {
    if (!selectedStudents.length) return "No student selected";
    if (selectedStudents.length === 1) {
      return enrollmentNo || "1 student";
    }
    return `${selectedStudents.length} students selected`;
  }, [selectedStudents.length, enrollmentNo]);

  if (!loggedIn) return <Redirect to="/Login" />;
  if (loading) return <MyLoader />;

  return (
    <div
      className="content payroll-page sm-page sr-page sic-page"
      style={{ background: COLORS.white, fontFamily: PAGE_FONT }}
    >
      <div
        className="content-header"
        style={{ background: COLORS.primary_color_shade5 }}
      >
        <div className="leftside-content-header">
          <ul className="breadcrumbs">
            <li>
              <i
                className="fa fa-home"
                aria-hidden="true"
                style={{ color: COLORS.white }}
              />
              <span style={{ color: COLORS.white }}> Student ID Card</span>
            </li>
          </ul>
        </div>
        <div className="rightside-content-header">
          <BackButton />
        </div>
      </div>

      <div className="bg_page_white_rounded box_shadow animated fadeInUp sm-shell">
        <header className="sm-hero" style={{ "--sm-hero-bg": HERO_GRADIENT }}>
          <div className="sm-hero__brand">
            <div className="sm-hero__icon" aria-hidden>
              <FaIdCard />
            </div>
            <div className="min-w-0">
              <h1 className="sm-hero__title">Student ID Card</h1>
              <p className="sm-hero__sub">
                {routeStudentId
                  ? `Auto preview for ${routeStudentId}`
                  : "Select student(s) and preview ID card"}
              </p>
            </div>
          </div>
          <div className="sm-hero__tools">
            <button
              type="button"
              className="sr-btn sr-btn--primary"
              onClick={() => setIsOpenSelectStudent(true)}
            >
              <FaUsers aria-hidden />
              {selectedStudents.length ? "Change students" : "Select students"}
            </button>
          </div>
        </header>

        <div className="sr-body sic-body">
          <section className="sic-student" aria-label="Selected student">
            <div className="sic-student__icon" aria-hidden>
              <FaUserGraduate />
            </div>
            <div className="sic-student__meta min-w-0">
              <span className="sic-student__label">Selected</span>
              <strong className="sic-student__value">{selectedCountLabel}</strong>
              {sessionId ? (
                <span className="sic-student__session">
                  Session · {sessionId}
                </span>
              ) : null}
            </div>
            <div className="sic-student__links">
              {enrollmentNo && sessionId ? (
                <Link
                  to={`/AdminMain/StudentRegSuccess/${encodeURIComponent(
                    enrollmentNo
                  )}/${encodeURIComponent(sessionId)}`}
                  className="sic-link"
                >
                  <FaCheck aria-hidden /> Success
                </Link>
              ) : null}
              <Link to="/AdminMain/StudentRegistration" className="sic-link">
                <FaUserPlus aria-hidden /> New registration
              </Link>
            </div>
          </section>

          {samples.length > 0 ? (
            <section className="sic-panel" aria-label="ID card samples">
              <div className="sic-panel__head">
                <FaIdCard aria-hidden />
                <h2>Sample</h2>
              </div>
              <div className="sic-samples">
                {samples.map((item, index) => {
                  const active = sampleIndex === index;
                  return (
                    <button
                      key={item.sample_id || index}
                      type="button"
                      className={`sic-sample${active ? " is-active" : ""}`}
                      onClick={() => handleSampleSelect(index)}
                    >
                      {item.sample_img ? (
                        <img src={item.sample_img} alt="" />
                      ) : (
                        <span className="sic-sample__fallback" aria-hidden>
                          <FaIdCard />
                        </span>
                      )}
                      <span>{item.sample_name || `Sample ${index + 1}`}</span>
                      {active ? <FaCheck className="sic-sample__check" /> : null}
                    </button>
                  );
                })}
              </div>
              {selectedSample?.sample_remark ? (
                <p className="sic-sample-remark">{selectedSample.sample_remark}</p>
              ) : null}
            </section>
          ) : null}

          <section className="sic-preview" aria-label="Preview">
            <div className="sic-preview__head">
              <h2>Preview</h2>
              {selectedSample?.sample_name ? (
                <span className="sic-preview__meta">
                  {selectedSample.sample_name}
                </span>
              ) : null}
            </div>
            <div className="sic-preview__frame">
              {previewLoading ? (
                <div className="sic-preview__loader">
                  <MyLoader />
                </div>
              ) : null}
              {fileUrl ? (
                <Iframe
                  url={fileUrl}
                  width="100%"
                  height={iframeHeight}
                  overflow="hidden"
                  scrolling="no"
                  id="iFrameStudentId"
                  className="sic-iframe"
                  display="initial"
                  position="relative"
                  onLoad={() => setPreviewLoading(false)}
                />
              ) : (
                <div className="sic-preview__empty">
                  Select a student to load the ID card preview.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>

      {isOpenSelectStudent ? (
        <MdlSelectMultipleStudent
          actionClose={() => setIsOpenSelectStudent(false)}
          actionHandleSelected={handleSelectedStudent}
        />
      ) : null}
    </div>
  );
}

export default StudentIdCard;
